import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  Typography, 
  Box, 
  Fade,
  Tooltip,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  DeleteOutline,
  CancelOutlined,
  SaveOutlined,
  ImageOutlined,
  VideoLibraryOutlined,
  UploadOutlined,
  EditOutlined,
  Crop,
} from '@mui/icons-material';

const ImageUploaderAndCrop = ({
  isDragging,
  handleDrop,
  handleDragOver,
  handleDragLeave,
  displayedImage,
  stagedImage,
  stagedImageFile,
  handleCancelImage,
  handleSaveImage,
  handleCropAgain,
  handleFileInput,
  onVideoSave,
  disabled,
  defaultVideoUrl,
  onRemoveImage,
  isEditing
}) => {
  const [mode, setMode] = useState('image');
  const [videoUrl, setVideoUrl] = useState('');
  const [isEditingVideo, setIsEditingVideo] = useState(false);
  const [tempVideoUrl, setTempVideoUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (defaultVideoUrl) {
      setVideoUrl(defaultVideoUrl);
      setTempVideoUrl(defaultVideoUrl);
      setMode('video');
    }
  }, [defaultVideoUrl]);

  // Styles
  const styles = {
    container: {
      width: '100%',
      minHeight: '180px',
      position: 'relative',
      padding: '12px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      backgroundColor: '#f9f9f9',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    },
    uploadArea: {
      width: '100%',
      height: displayedImage ? '130px' : '100%',
      border: `2px dashed ${isDragging ? '#1976d2' : '#e0e3e7'}`,
      borderRadius: '8px',
      position: 'relative',
      transition: 'all 0.2s ease',
      backgroundColor: !disabled ? 'rgb(245, 245, 245)' : '#e0e0e0',
      cursor: !disabled ? 'pointer' : 'auto',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        borderColor: !disabled ? '#1976d2' : '#e0e3e7',
        backgroundColor: !disabled ? 'rgb(240, 240, 240)' : '#e0e0e0',
      },
    },
    imagePreview: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '6px',
      display: 'block',
      transition: 'all 0.3s ease',
    },
    actionButtons: {
      position: 'absolute',
      top: 0,
      right: 0,
      padding: '4px',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      borderRadius: '0 0 0 6px',
      display: 'flex',
      gap: '4px',
      zIndex: 2,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      },
    },
    uploadContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '130px',
      padding: '16px',
      textAlign: 'center',
    },
    uploadIcon: {
      fontSize: '36px',
      color: '#9ca3af',
      marginBottom: '8px',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: '#6b7280',
      },
    },
    uploadText: {
      color: '#6b7280',
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    fileInput: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      cursor: !disabled ? 'pointer' : 'auto',
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      borderRadius: '6px',
      zIndex: 3,
    },
    tooltip: {
      backgroundColor: 'white',
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      '& .MuiTooltip-arrow': {
        color: 'white',
      },
    },
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
  };

  const handleVideoSave = async () => {
    const urlToSave = isEditingVideo ? tempVideoUrl.trim() : videoUrl.trim();
    try {
      setIsUploading(true);
      await onVideoSave(urlToSave);
      setVideoUrl(urlToSave);
      setIsEditingVideo(false);
    } catch (error) {
      console.error('Video save error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleVideoCancel = () => {
    setTempVideoUrl(videoUrl);
    setIsEditingVideo(false);
  };

  const handleEditStart = () => {
    setTempVideoUrl(videoUrl);
    setIsEditingVideo(true);
  };

  const isYouTubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/).+/;
    return youtubeRegex.test(url);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (url.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${url.split('/').pop()}`;
    }
    return `https://www.youtube.com/embed/${url.split('v=')[1]?.split('&')[0]}`;
  };

  // Dynamically size the container if no image
  const containerWidth = displayedImage ? '180px' : '160px';
  const containerHeight = displayedImage ? '180px' : '80px';

  return (
    <Paper elevation={0} sx={styles.container}>
      {/* If you want tabs for Image vs Video, uncomment these or style them differently */}
      {/* 
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <Button
          startIcon={<ImageOutlined />}
          variant={mode === 'image' ? 'contained' : 'outlined'}
          onClick={() => handleModeChange('image')}
          disabled={disabled}
          sx={{ flex: 1, marginRight: '8px' }}
        >
          Image
        </Button>
        <Button
          startIcon={<VideoLibraryOutlined />}
          variant={mode === 'video' ? 'contained' : 'outlined'}
          onClick={() => handleModeChange('video')}
          disabled={disabled}
          sx={{ flex: 1 }}
        >
          Video URL
        </Button>
      </Box>
      */}

      {/* Image Uploader */}
      <Box
        sx={styles.uploadArea}
        onDrop={!disabled ? handleDrop : undefined}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDragLeave={!disabled ? handleDragLeave : undefined}
      >
        {/* Remove Image Icon */}
        {displayedImage && !stagedImageFile && isEditing && (
          <Fade in timeout={300}>
            <Box sx={styles.actionButtons}>
              <Tooltip title="Remove image" arrow>
                <IconButton
                  size="small"
                  sx={{ color: '#fff' }}
                  onClick={onRemoveImage}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        )}

        {displayedImage ? (
          <>
            <img
              src={displayedImage}
              alt="Uploaded"
              style={styles.imagePreview}
            />

            {displayedImage && stagedImageFile && (
              <Fade in timeout={300}>
                <Box sx={styles.actionButtons}>
                  <Tooltip title="Cancel" arrow>
                    <IconButton
                      size="small"
                      sx={{ color: '#fff' }}
                      onClick={handleCancelImage}
                    >
                      <CancelOutlined />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Save" arrow>
                    <IconButton
                      size="small"
                      sx={{ color: '#fff' }}
                      onClick={handleSaveImage}
                    >
                      <SaveOutlined />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Crop again" arrow>
                    <IconButton
                      size="small"
                      sx={{ color: '#fff' }}
                      onClick={handleCropAgain}
                    >
                      <Crop />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Fade>
            )}
          </>
        ) : (
          <Box sx={styles.uploadContent}>
            <UploadOutlined sx={styles.uploadIcon} />
            <Typography sx={styles.uploadText}>
              Drop image here or click to upload
              <br />
              (png/jpeg/jpg)
            </Typography>
          </Box>
        )}

        <input
          id="imageUpload"
          type="file"
          accept="image/*"
          onChange={!disabled ? handleFileInput : undefined}
          style={styles.fileInput}
          disabled={disabled}
        />

        {/* Loading Overlay */}
        {isUploading && (
          <Box sx={styles.loadingOverlay}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ImageUploaderAndCrop;
